import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest } from 'rxjs';
import { debounceTime, startWith } from 'rxjs/operators';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { MaskService, Masks } from '../../../../../shared/services/mask.service';
import { TipoPessoaEnum } from '../../../../../shared/enums/pessoa/tipo-pessoa.enum';
import { TipoContatoEnum } from '../../../../../shared/enums/pessoa/tipo-contato.enum';
import { environment } from '../../../../../../environments/environment';
import { PessoaService } from '../../../../../shared/services/apis/pessoa/pessoa.service';
import { UtilService, CEPCorreios } from '../../../../../shared/services/util.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { LookupItemStruct } from '../../../../../shared/services/structs/pessoa/lookup-item.struct';
import { UnidadeFederativaService } from '../../../../../shared/services/apis/usuario/unidade-federativa.service';
import { MunicipioItem } from '../../../../../shared/services/apis/usuario/municipio.service';

interface UfSelectItem {
  id: string;
  descricao: string;
  idUnidadeFederativa: number;
}

@Component({
  selector: 'app-servico-tomado-emitente',
  standalone: true,
  imports: [
    ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatIconModule,
    MatButtonModule, SelectPesquisavelComponent, NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
  templateUrl: './servico-tomado-emitente.component.html',
})
export class ServicoTomadoEmitenteComponent implements OnInit {
  @Input({ required: true }) form!: FormGroup;

  public tipoPessoaEnum = TipoPessoaEnum;
  public isLoadingCep = false;

  public masks: Masks = MaskService.getMasks();

  /**
   * CPF/CNPJ não usa ngx-mask: o CNPJ passou a ser alfanumérico e a transição CPF→CNPJ trava
   * com máscara fixa. O MaskService formata no evento e guarda o valor já formatado no controle
   * — os separadores são removidos ao montar o request.
   */
  public onDocumentoInput(event: Event): void {
    MaskService.onDocumentoInput(event, this.form.get('identificacaoFiscal'));
  }

  public listaUf: UfSelectItem[] = [];
  public preloadedCity: MunicipioItem[] = [];

  public readonly urlFornecedor = `${environment.urlApiPessoa}Lookup/listPessoaSelect?idTipoContato=${TipoContatoEnum.Fornecedor}`;

  constructor(
    private dialog: MatDialog,
    private pessoaService: PessoaService,
    private utilService: UtilService,
    private alertService: AlertService,
    private unidadeFederativaService: UnidadeFederativaService,
  ) { }

  ngOnInit(): void {
    this.unidadeFederativaService.getUnidadesFederativas().subscribe(lista => {
      this.listaUf = lista.map(x => ({
        id: x.id,
        descricao: x.descricaoExibicao,
        idUnidadeFederativa: x.idUnidadeFederativa,
      }));
    });

    const ufControl = this.form.get('uf');
    const municipioControl = this.form.get('codigoMunicipioIbge');
    const municipioNomeControl = this.form.get('municipioNome');

    ufControl?.valueChanges.pipe(startWith(ufControl.value)).subscribe(uf => {
      if (uf) municipioControl?.enable({ emitEvent: false });
      else municipioControl?.disable({ emitEvent: false });
    });

    combineLatest([
      ufControl!.valueChanges.pipe(startWith(ufControl!.value)),
      municipioControl!.valueChanges.pipe(startWith(municipioControl!.value)),
      municipioNomeControl!.valueChanges.pipe(startWith(municipioNomeControl!.value)),
    ]).pipe(debounceTime(0)).subscribe(([uf, codIbge, nome]) => {
      this.preloadedCity = codIbge ? [{
        idMunicipio: 0,
        codIbge,
        nome: nome || '',
        uf: uf || null,
        idUnidadeFederativa: this.listaUf.find(u => u.id === uf)?.idUnidadeFederativa ?? 0,
      }] : [];
    });
  }

  public get docLabel(): string {
    return this.form.get('tipoPessoaEmitente')?.value === this.tipoPessoaEnum.Fisica ? 'CPF' : 'CNPJ';
  }

  public get urlMunicipio(): string {
    const ufSigla = this.form.get('uf')?.value;
    const ufItem = this.listaUf.find(u => u.id === ufSigla);
    return `${environment.urlApiUsuario}Municipio/select?idUnidadeFederativa=${ufItem?.idUnidadeFederativa ?? 0}`;
  }

  public changeUnidadeFederativa(item: UfSelectItem | null): void {
    this.form.patchValue({ codigoMunicipioIbge: null, municipioNome: null });
  }

  public changeMunicipio(item: MunicipioItem | null): void {
    if (!item) return;
    this.form.patchValue({ municipioNome: item.nome });
  }

  /** Ao selecionar um emitente já cadastrado, busca os dados completos (Pessoa/Lookup só traz id+nome). */
  public onEmitenteSelecionado(item: LookupItemStruct | LookupItemStruct[]): void {
    const selecionado = Array.isArray(item) ? item[0] : item;
    if (!selecionado) return;

    this.pessoaService.getById(selecionado.id).subscribe({
      next: (res) => {
        if (res.isError || !res.pessoa) return;
        const pessoa = res.pessoa;

        if (pessoa.idTipoPessoa === this.tipoPessoaEnum.Estrangeiro) {
          this.alertService.show('Atenção', 'O Emitente da NFS-e não suporta pessoa estrangeira (fora do leiaute nacional). Selecione um emitente nacional.', AlertType.warning);
          return;
        }

        this.form.patchValue({
          tipoPessoaEmitente: pessoa.idTipoPessoa,
          // Vem sem formatação da API; o controle guarda o valor já formatado.
          identificacaoFiscal: MaskService.formatDocumento(pessoa.cnpj || pessoa.cpf || ''),
          nomeEmitente: pessoa.nome,
          nomeFantasia: pessoa.nomeFantasia,
          inscricaoMunicipal: pessoa.inscricaoMunicipal,
          telefone: pessoa.telefone,
          email: pessoa.emailNFe,
        });
      }
    });
  }

  public buscarCep(): void {
    const cep = this.form.get('cep')?.value;
    if (!cep || cep.length < 8) return;

    this.isLoadingCep = true;
    this.utilService.getAddressByCEP(cep).subscribe({
      next: (res: CEPCorreios) => {
        this.isLoadingCep = false;
        if (!res || !res.cep) {
          this.alertService.show('Aviso', 'CEP não encontrado.', AlertType.warning);
          return;
        }
        this.form.patchValue({
          logradouro: res.logradouro,
          bairro: res.bairro,
          municipioNome: res.localidade,
          uf: res.uf,
          codigoMunicipioIbge: res.ibge,
        });
      },
      error: () => { this.isLoadingCep = false; }
    });
  }
}
